import classNames from 'classnames';
import { PropsWithChildren, useState, useEffect } from 'react';

import Accordion from '~components/Accordion/Accordion';
import AccordionItem from '~components/Accordion/AccordionItem';
import ZipForm from '~components/ZipCodeForm/ZipCodeForm';
import { fireTrackEvent } from '~globals/tracking';
import { useAppDispatch } from '~hooks/useRedux';
import { updatePlanType } from '~redux/Actions/GlobalActions';

interface IProps {
    stateName?: string | undefined;
    planYear?: string | undefined;
    zipFormHeading?: string | undefined;
    zipFormMainCtaCopy?: string | undefined;
    zipFormSecondaryCtaCopy?: string | undefined;
    zipFormQaValue?: string;
}

const ZipWithPdpMapd = ({
    stateName = 'your state',
    planYear = '',
    zipFormHeading = '',
    zipFormMainCtaCopy = '',
    zipFormSecondaryCtaCopy = '',
    zipFormQaValue = '',
}: PropsWithChildren<IProps>) => {
    const dispatch = useAppDispatch();

    const heading = zipFormHeading || `See plans from the nation's top health insurance companies`;
    const [planType, setPlanType] = useState('MA');

    const onPlanTypeChange = (planType: PlansType) => {
        setPlanType(planType);
        dispatch(updatePlanType(planType));
        trackPlanTypeSelection(planType);
    };

    useEffect(() => {
        dispatch(updatePlanType(planType));
    }, []);

    const trackPlanTypeSelection = (planType: PlansType) => {
        fireTrackEvent({ page: { action: `radio selection: ${planType} Plans` } });
    };

    const trackDiscoverLinkClick = () => {
        fireTrackEvent({ page: { action: `Discover better match` } });
    };

    return (
        <div className="pb-2 md:pb-6 lg:pb-10 lg:px-4">
            <section className={classNames('hero-paid-search-legacy-style-vertical-layout')}>
                <div className="hidden lg:block">
                    <h2>{heading}</h2>
                </div>
                <div className="block lg:hidden">
                    <div className="font-poppins text-16 leading-24 font-bold text-default text-center mb-3">
                        Select a plan type to compare:
                    </div>
                </div>

                <div className={classNames('pb-2 md:pb-0')}>
                    {/* -- Mobile View -- */}
                    <div className="block md:hidden w-full">
                        <div className="md:max-w-screen-md lg:max-w-screen-lg">
                            <Accordion>
                                <AccordionItem
                                    key="0"
                                    id="ZipFormMAAccordionItem"
                                    title={
                                        <span className="px-3 py-1 text-14">
                                            Medicare Advantage Plans
                                        </span>
                                    }
                                    theme="shine"
                                    expanded="true"
                                    data-wa-link=""
                                    waTrackingAction="Accordion: MA Plan"
                                    className={classNames(
                                        'rounded-lg bg-surface-light shadow pb-3 mb-3'
                                    )}
                                >
                                    {/* -- MA Plans -- */}
                                    <div className="p-3 max-w-screen-xs mx-auto">
                                        <ZipForm
                                            buttonTitle="See plans in my area"
                                            buttonTitleMobile="See plans in my area"
                                            buttonTitleMobileSuperSmall="See plans"
                                            buttonWaLink="Hero: PDPtoMA: See plans in my area: MA Plans"
                                            qaValue={`HeroStepper${
                                                zipFormQaValue && `-${zipFormQaValue}`
                                            }`}
                                            opts={{
                                                btnStyle: 'primary',
                                                useWideLayout: true,
                                                forceVerticalLayout: true,
                                            }}
                                            redirect={{
                                                toUrl: process.env.GATSBY_EHMP_URL,
                                                delay: 500,
                                                planTypeOverride: 'MA',
                                            }}
                                        />
                                        {/* -- / MA Plans -- */}
                                    </div>
                                </AccordionItem>
                                <AccordionItem
                                    key="1"
                                    id="ZipFormMDAccordionItem"
                                    title={
                                        <span className="px-3 py-1 text-14">
                                            Prescription Drug Plans
                                        </span>
                                    }
                                    theme="shine"
                                    data-wa-link=""
                                    waTrackingAction="Accordion: MD Plan"
                                    className={classNames(
                                        'rounded-lg bg-surface-light shadow pb-3 mb-3'
                                    )}
                                >
                                    <div className="p-3 max-w-screen-xs mx-auto">
                                        {/* -- MD Plans -- */}
                                        <ZipForm
                                            buttonTitle="See plans in my area"
                                            buttonTitleMobile="See plans in my area"
                                            buttonTitleMobileSuperSmall="See plans"
                                            buttonWaLink="Hero: PDPtoMA: See plans in my area: MD Plans"
                                            qaValue={`HeroStepper${
                                                zipFormQaValue && `-${zipFormQaValue}`
                                            }`}
                                            opts={{
                                                btnStyle: 'primary',
                                                useWideLayout: true,
                                                forceVerticalLayout: true,
                                            }}
                                            redirect={{
                                                toUrl: process.env.GATSBY_EHMP_URL,
                                                delay: 500,
                                                planTypeOverride: 'MD',
                                            }}
                                        />
                                        {/* -- / MD Plans -- */}
                                    </div>
                                </AccordionItem>
                            </Accordion>
                        </div>
                    </div>
                    {/* -- Mobile View -- */}

                    {/* -- Non-Mobile View -- */}
                    <div className="hidden md:flex md:justify-center">
                        <div className="max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg w-full grid grid-cols-12">
                            <div className="md:col-span-6 lg:col-span-5 border-r mr-6">
                                <div className="pb-3">
                                    <div className="font-poppins text-18 font-bold pb-3">
                                        Select a plan type to compare:
                                    </div>
                                    <form>
                                        <div>
                                            <label className="text-18">
                                                <input
                                                    type="radio"
                                                    name="planType"
                                                    value="MA"
                                                    id="planTypeMARadio"
                                                    onChange={() => onPlanTypeChange('MA')}
                                                    defaultChecked={planType === 'MA'}
                                                />{' '}
                                                Medicare Advantage Plans
                                            </label>
                                        </div>
                                        <div className="mt-2">
                                            <label className="text-18">
                                                <input
                                                    type="radio"
                                                    name="planType"
                                                    value="MD"
                                                    id="planTypeMDRadio"
                                                    onChange={() => onPlanTypeChange('MD')}
                                                    defaultChecked={planType === 'MD'}
                                                />{' '}
                                                Prescription Drug Plans
                                            </label>
                                        </div>
                                    </form>
                                </div>
                            </div>
                            <div className="md:col-span-6 lg:col-span-7 lg:flex lg:items-center">
                                <div className="md:block lg:hidden">
                                    {/* -- Form in Tablet View -- */}
                                    <ZipForm
                                        buttonTitle="See plans in my area"
                                        buttonTitleMobile="See plans in my area"
                                        buttonWaLink={`Hero: PDPtoMA: See plans in my area: ${planType} Plans`}
                                        qaValue={`HeroStepper${
                                            zipFormQaValue && `-${zipFormQaValue}`
                                        }`}
                                        opts={{
                                            btnStyle: 'primary',
                                            useWideLayout: true,
                                            forceVerticalLayout: true,
                                        }}
                                        redirect={{
                                            toUrl: process.env.GATSBY_EHMP_URL,
                                            delay: 500,
                                        }}
                                    />
                                    {/* -- / Form in Tablet View -- */}
                                </div>
                                <div className="md:hidden lg:block">
                                    {/* -- Form in Desktop View -- */}
                                    <ZipForm
                                        buttonTitle="See plans in my area"
                                        buttonTitleMobile="See plans in my area"
                                        buttonWaLink={`Hero: PDPtoMA: See plans in my area: ${planType} Plans`}
                                        qaValue={`HeroStepper${
                                            zipFormQaValue && `-${zipFormQaValue}`
                                        }`}
                                        opts={{
                                            btnStyle: 'primary',
                                            useWideLayout: true,
                                            forceVerticalLayout: false,
                                        }}
                                        redirect={{
                                            toUrl: process.env.GATSBY_EHMP_URL,
                                            delay: 500,
                                        }}
                                    />
                                    {/* -- / Form in Desktop View -- */}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* -- / Non-Mobile View -- */}
                </div>
                <div className="text-center px-4 lg:px-0">
                    <p className="text-18 mt-2 md:mt-5">
                        All-in-one Medicare Advantage or Stand-alone prescription drug plans? We can
                        help you choose.
                    </p>
                    <p className="mt-6 lg:mt-8 px-4 md:px-0 font-poppins font-medium text-16 text-primary">
                        <a href="#plansComparisonTableAnchor" onClick={trackDiscoverLinkClick}>
                            Discover which is the better match for you.
                        </a>
                    </p>
                </div>
            </section>
        </div>
    );
};

export default ZipWithPdpMapd;
